import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { ReqMethods, ResponseStatus } from "../enums";
// import { setNotification, useNotifcation } from "context/notification";
import { useAuth, useLogout } from "./auth";
import { notifyError, notifySuccess } from "Common/notify";

const BASE_URL = `${process.env.REACT_APP_API_URL}/api/v1`;
type RequestOptions = {
  query?: any | string;
  payload?: any;
  method?: any;
  token?: boolean;
  apiKey?: string | null;
  message?: string;
  error?: string;
  toaster?: string;
};
function useServiceHandler() {
  // const [, dispatch] = useNotifcation();
  const Logout = useLogout();
  return (
      fn: (
        method: string,
        path: string,
        config: AxiosRequestConfig | RequestOptions
      ) => Promise<any>
    ) =>
    async (
      method: string,
      path: string,
      {
        query,
        payload,
        token,
        apiKey,
        message,
        error,
        toaster,
      }: RequestOptions = {}
    ) => {
      try {
        const res = await fn(method, path, { query, payload, token, apiKey });
        // console.log("API - RESPONSE", res, message, error);
        // console.log(toaster,'toaster')
        toaster && notifySuccess(message || res.data.message);

        return { response: res.data };
      } catch (err: any) {
        // console.log('API- ERROR', err.response?.data || err);

        // expire error : jwt expired
        if (
          err.response &&
          err.response.status === ResponseStatus.UNAUTHORIZED
        ) {
          notifyError(error || "session expired");
          setTimeout(Logout, 4000);
          return { error: err.response?.data || err };
        } else {
          toaster && notifyError(err?.response?.data?.message);
        }

        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const authToken = useAuth();
  const serviceHandler = useServiceHandler();

  const CallService = (
    method: string | any,
    path: string,
    { query, payload, token = true, apiKey = null }: RequestOptions | any
  ) => {
    const pathname = query ? `${path}?${query}` : path;

    const config: AxiosRequestConfig = {};
    if (token)
      config.headers = { "x-auth-token": `Bearer ${authToken || token}` };
    if (apiKey) config.headers = { apiKey };
    type DetailsOptions = {
      payload?: any;
      config?: any;
    };
    const details: DetailsOptions = {};

    if (payload) details.payload = payload;
    details.config = config;

    return (axios as any)[method](pathname, ...Object.values(details));
  };

  return serviceHandler(CallService);
}

function useAppServices() {
  const { GET, POST, PUT, DELETE } = ReqMethods;
  const serviceHandler = useCallService();

  /**
   * Option for service is the object that could have the following properties
   * query, payload, token, apiKey
   */

  const APIs = {
    auth: {
      login: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/auth/login`, options),
      signup: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/auth/signup`, options),
    },
    dashboard: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/dashboard`, options),
    },
    company: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/company`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/company/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/company`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/company`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/company`, options),
    },
    sub_account: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/sub_account`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/sub_account/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/sub_account`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/sub_account`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/sub_account`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/sub_account/bulk_action`, options),
    },
    user: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/user`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/user/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/user`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/user`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/user`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/user/bulk_action`, options),
      change_password: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/user/change_password`, options),
    },
    contact: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/contact`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/contact/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/contact`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/contact`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/contact`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/contact/bulk_action`, options),
    },
    phone_bridge: {
      devices: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/phone_bridge/devices`, options),
        filter: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/phone_bridge/devices/filter`,
            options
          ),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/phone_bridge/devices`, options),
      },
      sim: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/phone_bridge/sim`, options),
        assignable_sim: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/phone_bridge/sim/assignable_sim`,
            options
          ),
        assignSim: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/phone_bridge/sim/assignSim`,
            options
          ),
      },
    },
    custom_field: {
      folder: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/custom_field/folder`, options),
        filter: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/custom_field/folder/filter`,
            options
          ),
        create: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/custom_field/folder`, options),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/custom_field/folder`, options),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/custom_field/folder`, options),
        bulk_action: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/custom_field/folder/bulk_action`,
            options
          ),
      },
      field: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/custom_field/field`, options),
        filter: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/custom_field/field/filter`, options),
        create: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/custom_field/field`, options),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/custom_field/field`, options),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/custom_field/field`, options),
        bulk_action: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/custom_field/field/bulk_action`,
            options
          ),
      },
    },
    custom_value: {
      folder: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/custom_value/folder`, options),
        filter: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/custom_value/folder/filter`,
            options
          ),
        create: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/custom_value/folder`, options),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/custom_value/folder`, options),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/custom_value/folder`, options),
        bulk_action: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/custom_value/folder/bulk_action`,
            options
          ),
      },
      value: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/custom_value/value`, options),
        filter: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/custom_value/value/filter`, options),
        create: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/custom_value/value`, options),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/custom_value/value`, options),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/custom_value/value`, options),
        bulk_action: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/custom_value/value/bulk_action`,
            options
          ),
      },
    },
    tag: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/tag`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/tag/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/tag`, options),
      update: (options: any) => serviceHandler(PUT, `${BASE_URL}/tag`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/tag`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/tag/bulk_action`, options),
    },
    note: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/note`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/note/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/note`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/note`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/note`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/note/bulk_action`, options),
    },
    task: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/task`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/task/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/task`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/task`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/task`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/task/bulk_action`, options),
    },
    conversation: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/conversation`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/conversation/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/conversation`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/conversation`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/conversation`, options),
    },
    message: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/message`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/message/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/message`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/message`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/message`, options),
    },
    stripe: {
      integrate_company: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/stripe/integrate_company`, options),
    },
    payment_method: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/payment_method`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/payment_method/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/payment_method`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/payment_method`, options),
      set_default: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/payment_method/set_default`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/payment_method`, options),
    },
    wallet: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/wallet`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/wallet/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/wallet`, options),
      add_balance: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/wallet/add_balance`, options),
      detuct_balance: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/wallet/detuct_balance`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/wallet`, options),
      update_auto_recharge: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/wallet/update_auto_recharge`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/wallet`, options),
    },
    transaction: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/transaction`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/transaction/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/transaction`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/transaction`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/transaction`, options),
    },
    service: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/service`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/service/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/service`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/service`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/service`, options),
    },
    reselling: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/reselling`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/reselling/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/reselling`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/reselling`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/reselling`, options),
    },
    saas_plan: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/saas_plan`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/saas_plan/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/saas_plan`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/saas_plan`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/saas_plan`, options),
    },
    saas_setting: {
      get: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/saas_setting`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/saas_setting/filter`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/saas_setting`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/saas_setting`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/saas_setting`, options),
    },
    webhook: {
      phone_bridge: {
        get_qr_code: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/webhook/phone_bridge/get-qr-code`,
            options
          ),
      },
      conversation_provider: {
        number_list: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/webhook/conversation_provider/number_list`,
            options
          ),
      },
    },
    app: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/app`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/app/filter`, options),
      apps_with_installed: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/app/apps_with_installed`, options),
      create: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/app`, options),
      update: (options: any) => serviceHandler(PUT, `${BASE_URL}/app`, options),
      delete: (options: any) =>
        serviceHandler(DELETE, `${BASE_URL}/app`, options),
      bulk_action: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/app/bulk_action`, options),
      category: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/app/category`, options),
        filter: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/app/category/filter`, options),
        create: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/app/category`, options),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/app/category`, options),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/app/category`, options),
        bulk_action: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/app/category/bulk_action`, options),
      },
      conversation_provider: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/app/conversation_provider`, options),
        filter: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/app/conversation_provider/filter`,
            options
          ),
        installed_providers: (options: any) =>
          serviceHandler(
            GET,
            `${BASE_URL}/app/conversation_provider/installed_providers`,
            options
          ),
        create: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/app/conversation_provider`,
            options
          ),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/app/conversation_provider`, options),
        delete: (options: any) =>
          serviceHandler(
            DELETE,
            `${BASE_URL}/app/conversation_provider`,
            options
          ),
        bulk_action: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/app/conversation_provider/bulk_action`,
            options
          ),
      },
      app_install: {
        get: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/app/app_install`, options),
        filter: (options: any) =>
          serviceHandler(GET, `${BASE_URL}/app/app_install/filter`, options),
        create: (options: any) =>
          serviceHandler(POST, `${BASE_URL}/app/app_install`, options),
        update: (options: any) =>
          serviceHandler(PUT, `${BASE_URL}/app/app_install`, options),
        delete: (options: any) =>
          serviceHandler(DELETE, `${BASE_URL}/app/app_install`, options),
        bulk_action: (options: any) =>
          serviceHandler(
            POST,
            `${BASE_URL}/app/app_install/bulk_action`,
            options
          ),
      },
    },
    utils: {
      upload_image: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/utils/upload/image`, options),
    },
  };

  return APIs;
}

const useUploadImage = () => {
  const AppService = useAppServices();
  return ({
    toaster,
    file,
    desiredPath,
  }: {
    toaster: any;
    file: any;
    desiredPath: string;
  }) => {
    const form = new FormData();
    form.append("image", file, file.name);
    return AppService.utils.upload_image({
      toaster,
      payload: form,
      query: `desiredPath=${desiredPath}`,
    });
  };
};

export { useAppServices, useCallService, useUploadImage };
